import { Component, HostBinding, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { AuthService } from "../services/auth.service";
import { BreakpointObserver } from "@angular/cdk/layout";
import { NavigationEnd, NavigationStart, Router, RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { EProtocolCurrentStatus, Protocol } from "../interfaces/protocols/protocol";
import { ToolbarServiceService } from "../services/toolbar-service.service";
import { UserService } from "../services/user.service";
import { MitpManagerService } from "../services/mitp-manager.service";
import { MatDialog } from "@angular/material/dialog";
import { Notification } from "../interfaces/notification";
import { NavigationSpecialtyItem } from "../interfaces/navigation-specialty-item";
import { OrderByPipe } from "ngx-pipes";
import { initializeApp } from "firebase/app";
import { environment } from "../../environments/environment";
import { LegalService } from "../services/legal.service";
import { NotificationsViewComponent } from "../modules/notifications/notifications-view/notifications-view.component";
import { MatIconButton } from "@angular/material/button";
import { MatDivider } from "@angular/material/divider";
import { MatPrefix } from "@angular/material/form-field";
import { MatListItem, MatListSubheaderCssMatStyler, MatNavList } from "@angular/material/list";
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from "@angular/material/sidenav";
import { MatIcon } from "@angular/material/icon";
import { MatToolbar } from "@angular/material/toolbar";

import { ThemeService } from "../services/theme.service";
import { NavigationSpecialtyService } from "../services/navigation-specialty.service";
import { UserBadgeComponent } from "../modules/users/user-badge/user-badge.component";
import { NgxTolgeeModule, TranslateService } from "@tolgee/ngx";
import { InstituteService } from "../services/institute.service";
import { ShowInDecemberDirective } from "../directives/show-in-december.directive";
import { EPathwayStatus } from "../modules/pathways/entities/pathway";
import { SnowComponent } from "../modules/misc/snow/snow.component";
import { MatProgressBar } from "@angular/material/progress-bar";
import { AsyncPipe } from "@angular/common";
import { HttpLoadingService } from "../services/http-loading.service";
import { JanusComponent } from "../modules/protocols/janus/janus.component";
import { ConnectivityService, EConnectivityStatus } from "../services/connectivity.service";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		MatToolbar,
		MatIcon,
		MatSidenavContainer,
		MatSidenav,
		MatNavList,
		MatListSubheaderCssMatStyler,
		MatListItem,
		RouterLinkActive,
		RouterLink,
		MatPrefix,
		MatDivider,
		MatSidenavContent,
		MatIconButton,
		NotificationsViewComponent,
		RouterOutlet,
		UserBadgeComponent,
		NgxTolgeeModule,
		ShowInDecemberDirective,
		SnowComponent,
		MatProgressBar,
		AsyncPipe,
	],
})
export class AppComponent implements OnInit, OnDestroy {
	authService = inject(AuthService);
	userService = inject(UserService);
	manager = inject(MitpManagerService);
	httpLoading = inject(HttpLoadingService);
	connectivity = inject(ConnectivityService);
	private legalService = inject(LegalService);
	private instituteService = inject(InstituteService);
	private navigationSpecialtyService = inject(NavigationSpecialtyService);
	private router = inject(Router);
	toolbarService = inject(ToolbarServiceService);
	private bpObserver = inject(BreakpointObserver);
	private translateService = inject(TranslateService);
	private dialog = inject(MatDialog);
	private oPipe = inject(OrderByPipe);
	themeService = inject(ThemeService);
	currentUrl = "";

	@HostBinding("class") className = "";
	protocol: Protocol;
	title = "MedInThePocket";
	description = "";
	navOpened = !this.isSmallScreen();
	imgURL: string | undefined;
	lang = "fr";
	notifications: Notification[] = [];
	navigationSpecialtyItems: NavigationSpecialtyItem[] = [];
	firebaseApp = initializeApp(environment.firebaseConfig);
	readonly isEducationDomain = this.detectEducationDomain();
	private readonly educationAllowedPrefixes = ["/education", "/misc/about", "/settings-user"];
	readonly lightropeLights: number[] = Array.from({ length: 42 }, (_, index) => index);

	constructor() {
		this.themeService.setTheme();
		this.currentUrl = this.router.url;
		this.themeService.themeControl.valueChanges.subscribe((darkMode) => {
			this.className = darkMode ? "darkTheme" : "";
		});
		this.translateService.languageAsync.subscribe((lang) => {
			const html = document.getElementsByTagName("html")[0];
			html.setAttribute("lang", lang);
			html.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
		});
	}

	ngOnInit(): void {
		this.connectivity.start();
		if (this.isEducationDomain) {
			this.navOpened = false;
		}
		if (this.authService.isAuth()) {
			this.loadLogo();
			this.loadNavigationItems();
			this.userService.loadCurrentUser();
			this.userService.fetchUserConfig();
			if (this.isEducationDomain) {
				this.redirectToEducation();
			}
			this.enforceEducationStudentAccess();
		}
		this.authService.authStatus.subscribe((status) => {
			if (status) {
				this.loadLogo();
				this.loadNavigationItems();
				this.userService.loadCurrentUser();
				this.themeService.checkUserTheme();
				this.legalService.requestCguNotice();
				this.userService.fetchUserConfig();
				if (this.isEducationDomain) {
					this.redirectToEducation();
				}
				this.enforceEducationStudentAccess();
			}
		});
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationStart) {
				if (this.isEducationDomain && this.authService.isAuth() && !this.isEducationAllowedUrl(event["url"])) {
					void this.router.navigate(["/education"]);
					this.closePatientIdDialog();
					return;
				}
				const regex = new RegExp("^/(protocols|glasses|pathways)/[0-9]+/edit$");
				if (regex.test(event["url"])) {
					this.toolbarService.protocolSubject.subscribe((protocol) => {
						this.title = protocol.label;
						switch (protocol.status) {
							case 0:
								this.description = this.translateService.instant(
									"protocol.status.description.disabled"
								);
								break;
							case 1:
								this.description = this.translateService.instant(
									"protocol.status.description.published"
								);
								break;
							case 2:
								this.description = this.translateService.instant(
									"protocol.status.description.archived"
								);
								break;
							default:
								this.description = this.translateService.instant("protocol.status.description.unknown");
						}
						if (protocol.current_status !== EProtocolCurrentStatus.published) {
							this.description += ` - ${this.translateService.instant("protocol.status.description.in-progress")}`;
						}
					});
					this.toolbarService.pathwaySubject.subscribe((pathway) => {
						this.title = pathway.label;
						switch (pathway.status) {
							case EPathwayStatus.draft:
								this.description = this.translateService.instant(
									"pathways.pathway.status.description.disabled"
								);
								break;
							case EPathwayStatus.published:
								this.description = this.translateService.instant(
									"pathways.pathway.status.description.published"
								);
								break;
							case EPathwayStatus.archived:
								this.description = this.translateService.instant(
									"pathways.pathway.status.description.archived"
								);
								break;
							default:
								this.description = this.translateService.instant(
									"pathways.pathway.status.description.unknown"
								);
						}
					});
				} else {
					this.title = "MedInThePocket";
					this.description = "";
				}
				this.closePatientIdDialog();
				if (!this.isEducationDomain && this.isSmallScreen() && this.navOpened) {
					this.navOpened = false;
				}
			}
			if (event instanceof NavigationEnd) {
				this.currentUrl = event.urlAfterRedirects;
			}
		});
	}

	closePatientIdDialog(): void {
		this.dialog.getDialogById("patientIdDialog")?.close();
	}

	openJanus(): void {
		this.dialog.open(JanusComponent, {
			width: "800px",
			height: "80vh",
			disableClose: true,
		});
	}

	toggle() {
		if (this.isEducationDomain) {
			return;
		}
		this.navOpened = !this.navOpened;
	}

	isSmallScreen() {
		return this.bpObserver.isMatched("(max-width: 900px)");
	}

	private loadLogo() {
		this.instituteService.getInstituteLogo(this.userService.getInstituteId()).subscribe((logo) => {
			if (this.imgURL) {
				URL.revokeObjectURL(this.imgURL);
			}
			const url = URL.createObjectURL(logo);
			this.imgURL = url;
		});
	}

	private loadNavigationItems(): void {
		this.navigationSpecialtyService.getMyProtocolNavigationItems().subscribe((items) => {
			this.navigationSpecialtyItems = this.oPipe.transform(items, "label");
			this.navigationSpecialtyService.setNavigationItems(items);
		});
	}

	private detectEducationDomain(): boolean {
		if (typeof window === "undefined" || !window.location?.hostname) {
			return false;
		}
		const hostname = window.location.hostname.toLowerCase();
		return hostname === "medinthepocket.education" || hostname.endsWith(".medinthepocket.education");
	}

	private redirectToEducation(): void {
		if (!this.isEducationAllowedUrl(this.router.url)) {
			void this.router.navigate(["/education"]);
		}
	}

	private isEducationAllowedUrl(url: string): boolean {
		return this.educationAllowedPrefixes.some((path) => url.startsWith(path));
	}

	private enforceEducationStudentAccess(): void {
		if (!this.isEducationDomain || !this.authService.isAuth()) {
			return;
		}
		if (!this.manager.isEducationStudent()) {
			const message = this.translateService.instant("education.portal.students-only");
			this.manager.openInfoDialog(message);
			this.authService.logout();
		}
	}

	getIcon(item: NavigationSpecialtyItem): string {
		if (item.icon.startsWith("material")) {
			const splittedString = item.icon.split("|");
			return splittedString[1];
		} else {
			return item.icon;
		}
	}

	navigateToHome(): void {
		this.router.navigateByUrl("/home");
	}

	isProtocolsNavActive(): boolean {
		const url = this.currentUrl || this.router.url;
		return url.startsWith("/protocols") && !this.isProtocolEditRoute(url) && !this.isProtocolsSubitemRoute(url);
	}

	isMyProtocolsNavActive(): boolean {
		const url = this.currentUrl || this.router.url;
		return url.startsWith("/my-protocols") || this.isProtocolEditRoute(url);
	}

	private isProtocolEditRoute(url: string): boolean {
		return /^\/protocols\/\d+\/edit/.test(url);
	}

	isProtocolsSubitemActive(itemId: number): boolean {
		const url = this.currentUrl || this.router.url;
		return this.getProtocolsSubitemIdFromUrl(url) === itemId;
	}

	private isProtocolsSubitemRoute(url: string): boolean {
		return this.getProtocolsSubitemIdFromUrl(url) !== null;
	}

	private getProtocolsSubitemIdFromUrl(url: string): number | null {
		const match = url.match(/^\/protocols\/subitems\/(\d+)(?:\/|$)/);
		return match ? Number(match[1]) : null;
	}

	ngOnDestroy(): void {
		this.connectivity.stop();
		if (this.imgURL) {
			URL.revokeObjectURL(this.imgURL);
		}
	}

	protected readonly EConnectivityStatus = EConnectivityStatus;
}
